import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { shopApi, productLabel } from "@/lib/shopApi";
import { money } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { ProductTile } from "@/components/store/Brand";

export function StockPill({ inStock }) {
  return inStock
    ? <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">In stock</span>
    : <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700">Out of stock</span>;
}

function ProductCard({ p, catName, i }) {
  const { addToCart } = useMember();
  const [added, setAdded] = useState(false);
  const add = () => { addToCart(p, 1); setAdded(true); toast.success(`${productLabel(p)} added to cart`); setTimeout(() => setAdded(false), 1200); };
  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(137,207,240,0.22)] animate-[fadeUp_.4s_ease-out_both]"
      style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }} data-testid={`product-card-${p.id}`}>
      <Link to={`/shop/product/${p.id}`} className="block aspect-[4/3] overflow-hidden border-b border-slate-100" data-testid={`product-card-link-${p.id}`}><ProductTile product={p} /></Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-700">{catName}</div>
        <Link to={`/shop/product/${p.id}`} className="mt-1 font-head text-base font-bold leading-snug text-slate-900 hover:text-sky-800">
          {p.name} {p.size && <span className="ml-1 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 align-middle font-mono text-[11px] font-semibold text-sky-800" data-testid={`product-card-size-${p.id}`}>{p.size}</span>}
        </Link>
        <div className="mt-auto flex items-end justify-between gap-2 pt-4">
          <div>
            <div className="font-head text-xl font-extrabold text-slate-900" data-testid={`product-card-price-${p.id}`}>{money(p.price)}</div>
            <div className="mt-1"><StockPill inStock={p.in_stock} /></div>
          </div>
          <button onClick={add} disabled={!p.in_stock} data-testid={`product-card-add-${p.id}`}
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#89CFF0] text-slate-900 transition-[background-color,transform] hover:bg-[#74C2EC] active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400" aria-label="Add to cart">
            {added ? <Check className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);

  useEffect(() => { shopApi.get("/shop/catalog").then((r) => setData(r.data)).catch(() => {}); }, []);
  const catNames = useMemo(() => Object.fromEntries((data?.categories || []).map((c) => [c.id, c.name])), [data]);
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.products || []).filter((p) => (cat === "all" || p.category_id === cat) && (!inStockOnly || p.in_stock)
      && (!term || `${p.name} ${p.size}`.toLowerCase().includes(term)));
  }, [data, cat, q, inStockOnly]);

  if (!data) return <div className="py-20 text-center text-slate-500">Loading catalogue…</div>;
  const pill = (active) => `shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`;

  return (
    <div data-testid="shop-page">
      <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-sky-700">Members catalogue</div>
          <h1 className="mt-1 font-head text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Shop</h1>
        </div>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center md:w-auto">
          <div className="relative w-full md:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products or MG…" data-testid="shop-search-input"
              className="h-11 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[#89CFF0]" />
          </div>
          <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} className="h-4 w-4 accent-sky-500" data-testid="shop-instock-toggle" /> In stock only
          </label>
        </div>
      </div>

      <div className="-mx-5 mt-6 flex gap-2 overflow-x-auto px-5 pb-2 sm:mx-0 sm:flex-wrap sm:px-0" data-testid="shop-category-filter">
        <button className={pill(cat === "all")} onClick={() => setCat("all")} data-testid="shop-category-all">All</button>
        {data.categories.map((c) => <button key={c.id} className={pill(cat === c.id)} onClick={() => setCat(c.id)} data-testid={`shop-category-${c.id}`}>{c.name}</button>)}
      </div>

      <p className="mt-4 text-sm text-slate-500" data-testid="shop-result-count">{list.length} product{list.length === 1 ? "" : "s"}</p>
      <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {list.map((p, i) => <ProductCard key={p.id} p={p} catName={catNames[p.category_id]} i={i} />)}
      </div>
      {list.length === 0 && <div className="mt-10 rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="shop-empty">No products match your search.</div>}
    </div>
  );
}
